import React, { useState } from 'react';
import './index.css';
import {
  EStandalonePages,
  MAGIC_COOKIES,
  STANDALONE_CONSTANTS,
} from './pages/StandAlone/constants';

// Cookie utility functions
export const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Detect if the app is running inside an iframe (cross-origin embed).
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin iframes throw on accessing window.top
    return true;
  }
};

const setCookie = (name: string, value: string, expires: number) => {
  // expires is a timestamp (seconds since epoch), use as max-age directly
  const expiresInSeconds = expires - Math.floor(Date.now() / 1000);
  // Use SameSite=None; Secure when embedded in an iframe so cookies are sent cross-origin
  const sameSiteAttr = isInIframe()
    ? 'SameSite=None; Secure'
    : 'SameSite=Strict';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; ${sameSiteAttr}; max-age=${expiresInSeconds}`;
};

export const deleteCookie = (name: string) => {
  const sameSiteAttr = isInIframe()
    ? 'SameSite=None; Secure'
    : 'SameSite=Strict';
  document.cookie = `${name}=; path=/; ${sameSiteAttr}; max-age=0`;
};

// Read query params, store in sessionStorage and set as cookies
// Standalone session logic using ConfirmationModal dialog
export const useStandaloneParams = () => {
  const [showModal, setShowModal] = useState(false);
  const [newParams, setnewParams] = useState<{
    sid: string | null;
    sig: string | null;
    exp: string | null;
    redirectOnSuccess: string;
  } | null>(null);
  const [params, setParams] = useState<{
    sid: string | null;
    sig: string | null;
    redirectOnSuccess: string;
    invalidNewAuth?: boolean;
  } | null>(null);

  let redirectOnSuccess = EStandalonePages.APPROVED_PRODUCTS; // Default redirect
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sid = urlParams.get(MAGIC_COOKIES.SID);
    const sig = urlParams.get(MAGIC_COOKIES.SIG);
    const sidFromCookie = getCookie(MAGIC_COOKIES.SID);
    const sigFromCookie = getCookie(MAGIC_COOKIES.SIG);
    const exp = urlParams.get('exp');
    redirectOnSuccess =
      EStandalonePages[urlParams.get('r') as keyof typeof EStandalonePages] ||
      EStandalonePages.APPROVED_PRODUCTS;

    const isValidAuth = (exp: number) => {
      if (exp > Math.floor(Date.now() / 1000)) return true;
      return false;
    };

    if (sid || sig) {
      if (sidFromCookie && sigFromCookie) {
        if (sidFromCookie === sid || sigFromCookie === sig) {
          setParams({
            sid: sidFromCookie,
            sig: sigFromCookie,
            redirectOnSuccess,
          });
        } else if (
          (sidFromCookie !== sid || sigFromCookie !== sig) &&
          isValidAuth(Number(exp))
        ) {
          setnewParams({ sid, sig, exp, redirectOnSuccess });
          setShowModal(true);
        } else {
          setParams({
            sid: sidFromCookie,
            sig: sigFromCookie,
            redirectOnSuccess,
            invalidNewAuth: true,
          });
          setShowModal(true);
        }
      } else if (exp && isValidAuth(Number(exp))) {
        // No cookies, set new session
        if (sid) setCookie(MAGIC_COOKIES.SID, sid, Number(exp));
        if (sig) setCookie(MAGIC_COOKIES.SIG, sig, Number(exp));
        setParams({ sid, sig, redirectOnSuccess });
      } else {
        setParams({ sid: null, sig: null, redirectOnSuccess });
      }
    } else if (sidFromCookie && sigFromCookie) {
      setParams({ sid: sidFromCookie, sig: sigFromCookie, redirectOnSuccess });
    } else {
      setParams({ sid: null, sig: null, redirectOnSuccess });
    }

    // Remove query params from URL after storing (for security/cleaner URL)
    if (sid || sig || exp) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  // Handler for modal confirmation
  const handleModalConfirm = () => {
    if (newParams) {
      if (newParams.sid)
        setCookie(MAGIC_COOKIES.SID, newParams.sid, Number(newParams.exp));
      if (newParams.sig)
        setCookie(MAGIC_COOKIES.SIG, newParams.sig, Number(newParams.exp));
      setParams({
        sid: newParams.sid,
        sig: newParams.sig,
        redirectOnSuccess,
      });
      setShowModal(false);
      setnewParams(null);
    }
  };
  // Handler for modal close/cancel
  const handleModalClose = () => {
    // Keep existing session (do not overwrite cookies)
    const sidFromCookie = getCookie(MAGIC_COOKIES.SID);
    const sigFromCookie = getCookie(MAGIC_COOKIES.SIG);
    setParams({
      sid: sidFromCookie,
      sig: sigFromCookie,
      redirectOnSuccess,
    });
    setShowModal(false);
    setnewParams(null);
  };

  return { params, showModal, handleModalConfirm, handleModalClose };
};

// Export for use in API calls (only sid and sig, exp is not sent to API)
export const getStandaloneParams = () => ({
  sid: getCookie(MAGIC_COOKIES.SID),
  sig: getCookie(MAGIC_COOKIES.SIG),
});

export const handleErrorClose = () => {
  window.location.href = EStandalonePages.LANDING; // Redirect to login page
};
